/**
 * Shared axios factory.
 *
 * Every API module calls createApiClient() instead of axios.create() directly.
 * This adds one request interceptor that stamps an X-Request-ID header on every
 * outgoing call so the backend RequestLogger can correlate FE and BE log lines
 * in Grafana using the same ID.
 *
 * Each module still adds its own response / error interceptors after calling
 * createApiClient() — nothing about that changes.
 */

import axios, { type CreateAxiosDefaults, type AxiosInstance } from 'axios';

import { config } from '@/config';

const IS_DEV = config.environment === 'development';
const DEV_ROLE_COOKIE = 'dev_assumed_role';

const generateRequestId = (): string => {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    // crypto.randomUUID() is unavailable in very old browsers / non-secure contexts
    return Math.random().toString(36).slice(2, 10);
  }
};

function readDevRoleCookie(): string | null {
  try {
    const match = document.cookie.split('; ').find((row) => row.startsWith(`${DEV_ROLE_COOKIE}=`));
    return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
  } catch {
    return null;
  }
}

/**
 * Create an axios instance pre-configured with X-Request-ID injection.
 *
 * In development builds, also injects an X-Dev-Role header carrying the stored
 * dev role override so the back end can apply it. Browser cookies are not
 * transmitted cross-origin without withCredentials, so the header is the
 * reliable channel for this local-dev-only signal.
 *
 * Usage:
 *   import { createApiClient } from '@/api/http-client';
 *   const myClient = createApiClient({ baseURL: config.apiUrl, timeout: 30_000 });
 *   myClient.interceptors.response.use(...);  // add your own handlers as usual
 */
export const createApiClient = (options: CreateAxiosDefaults = {}): AxiosInstance => {
  const client = axios.create({ withCredentials: true, ...options });

  client.interceptors.request.use((cfg) => {
    cfg.headers['X-Request-ID'] = generateRequestId();
    if (IS_DEV) {
      const url = cfg.url ?? '';
      const isAuthPath = url.includes('/auth/');
      if (!isAuthPath) {
        const devRole = readDevRoleCookie();
        if (devRole) {
          cfg.headers['X-Dev-Role'] = devRole;
        }
      }
    }
    return cfg;
  });

  return client;
};
